"use strict";
// @df/api - Authentication state management
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.authManager = void 0;
// Single-flight refresh to prevent multiple simultaneous refresh attempts
let inflightRefresh = null;
// Flag to track if refresh has permanently failed (prevents endless retries)
let refreshPermanentlyFailed = false;
class AuthManager {
    constructor() {
        this.state = {
            accessToken: null,
            refreshToken: null,
            lastLoginAt: null,
            isSuspect: false
        };
        // Refresh token this tab is currently submitting. Tabs share localStorage but each keeps its own
        // in-memory pair, and the backend rotates and blacklists refresh tokens, so a stale tab's failed
        // refresh must not delete a newer pair another tab persisted.
        this.refreshingWith = null;
        // Number of clearTokens calls, so a refresh can tell whether tokens were cleared while in flight.
        this.clearCount = 0;
        // A newer pair from another tab that clearTokens removed while this tab's refresh was in flight.
        this.pairClearedDuringRefresh = null;
        // Rehydrate tokens from localStorage on initialization
        this.rehydrateFromStorage();
    }
    // Rehydrate tokens from localStorage
    rehydrateFromStorage() {
        try {
            const accessToken = localStorage.getItem('access');
            const refreshToken = localStorage.getItem('refresh');
            if (accessToken && refreshToken) {
                this.state.accessToken = accessToken;
                this.state.refreshToken = refreshToken;
                this.state.lastLoginAt = null; // Don't restore timestamp
                this.state.isSuspect = false;
            }
        }
        catch (error) {
            console.warn('Failed to rehydrate tokens from localStorage:', error);
        }
    }
    // Get access token
    getAccessToken() {
        return this.state.accessToken;
    }
    // Get refresh token
    getRefreshToken() {
        return this.state.refreshToken;
    }
    // Set access token
    setAccessToken(token) {
        this.state.accessToken = token;
    }
    // Set refresh token
    setRefreshToken(token) {
        this.state.refreshToken = token;
    }
    // Set both tokens
    setTokens(accessToken, refreshToken) {
        console.log('[Trace] setTokens called', {
            hasAccess: !!accessToken,
            hasRefresh: !!refreshToken,
            accessExp: accessToken ? this.getTokenExpiration(accessToken) : null,
            timestamp: new Date().toISOString()
        });
        this.state.accessToken = accessToken;
        this.state.refreshToken = refreshToken;
        this.state.lastLoginAt = Date.now();
        this.state.isSuspect = false;
        // Reset refresh failure flag when new tokens are set (e.g., after login or successful refresh)
        refreshPermanentlyFailed = false;
        // Persist to localStorage
        try {
            localStorage.setItem('access', accessToken);
            localStorage.setItem('refresh', refreshToken);
        }
        catch (error) {
            console.warn('Failed to save tokens to localStorage:', error);
        }
    }
    // Clear all tokens
    clearTokens() {
        console.log('[Trace] clearTokens called', {
            timestamp: new Date().toISOString()
        });
        this.state.accessToken = null;
        this.state.refreshToken = null;
        this.state.lastLoginAt = null;
        this.state.isSuspect = false;
        // Reset refresh failure flag when tokens are cleared
        // This allows refresh to work again after a new login
        refreshPermanentlyFailed = false;
        // Clearing is always immediate (logout must take effect even if the page then unloads). While
        // this tab's refresh is in flight, remember a newer pair from another tab that this removes:
        // if the clear was only the client's cleanup of that refresh's 401, refreshOnce puts it back.
        this.clearCount += 1;
        if (this.refreshingWith && !this.pairClearedDuringRefresh) {
            this.pairClearedDuringRefresh = this.readNewerPersistedPair(this.refreshingWith, true);
        }
        // Clear from localStorage
        try {
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
        }
        catch (error) {
            console.warn('Failed to clear tokens from localStorage:', error);
        }
    }
    // Check if user is authenticated
    isAuthenticated() {
        const result = !!this.state.accessToken;
        console.log('[Trace] isAuthenticated called', {
            result,
            hasAccessToken: !!this.state.accessToken,
            hasRefreshToken: !!this.state.refreshToken,
            isSuspect: this.state.isSuspect,
            timestamp: new Date().toISOString()
        });
        return result;
    }
    // Get authorization header
    getAuthHeader() {
        if (this.state.accessToken) {
            return `Bearer ${this.state.accessToken}`;
        }
        return null;
    }
    // Check if user just logged in within grace period
    justLoggedIn(graceMs = 3000) {
        if (!this.state.lastLoginAt)
            return false;
        return (Date.now() - this.state.lastLoginAt) < graceMs;
    }
    // Mark tokens as suspect (for soft failure handling)
    markSuspect() {
        this.state.isSuspect = true;
    }
    // Clear suspect state without touching tokens
    clearSuspect() {
        this.state.isSuspect = false;
    }
    // Check if tokens are marked as suspect
    isSuspect() {
        return this.state.isSuspect;
    }
    // Get token expiration time from JWT
    getTokenExpiration(token) {
        try {
            // JWT format: header.payload.signature
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            // Decode the payload (second part); JWTs use base64url, which atob does not accept
            const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            // Add padding if needed for base64 decoding
            const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
            const decoded = atob(paddedPayload);
            const parsed = JSON.parse(decoded);
            // Extract expiration claim (exp)
            if (typeof parsed.exp === 'number') {
                return parsed.exp;
            }
            return null;
        }
        catch (error) {
            // If decoding fails, return null
            return null;
        }
    }
    // The pair another tab persisted, if it supersedes `refreshToken`: both tokens present, decodable,
    // and a refresh token that expires later. exp has whole-second precision, so a rotation in the same
    // second as the token it replaces has an equal exp; `allowEqualExpiry` accepts that once the server
    // has rejected `refreshToken`. An older pair, such as one handed over again by the Auth Portal, is
    // never adopted. The server still validates whatever is sent; this only chooses which pair to send.
    readNewerPersistedPair(refreshToken, allowEqualExpiry = false) {
        if (!refreshToken) {
            return null;
        }
        let accessToken = null;
        let persistedRefreshToken = null;
        try {
            accessToken = localStorage.getItem('access');
            persistedRefreshToken = localStorage.getItem('refresh');
        }
        catch (error) {
            return null;
        }
        if (!accessToken || !persistedRefreshToken || persistedRefreshToken === refreshToken) {
            return null;
        }
        const persistedExp = this.getTokenExpiration(persistedRefreshToken);
        const currentExp = this.getTokenExpiration(refreshToken);
        if (persistedExp === null || currentExp === null || this.getTokenExpiration(accessToken) === null) {
            return null;
        }
        if (persistedExp < currentExp || (persistedExp === currentExp && !allowEqualExpiry)) {
            return null;
        }
        return { accessToken, refreshToken: persistedRefreshToken };
    }
    // Use a pair another tab already persisted; storage is left as it is.
    adoptPersistedPair(pair) {
        this.state.accessToken = pair.accessToken;
        this.state.refreshToken = pair.refreshToken;
        this.state.isSuspect = false;
        refreshPermanentlyFailed = false;
        console.log('[Trace] adopted newer persisted tokens', {
            timestamp: new Date().toISOString()
        });
    }
    // Single-flight refresh to prevent multiple simultaneous refresh attempts
    async refreshOnce() {
        // Another tab may already have rotated this tab's refresh token: use its pair rather than
        // submitting a token the backend has blacklisted.
        const persistedPair = this.readNewerPersistedPair(this.state.refreshToken);
        if (persistedPair) {
            this.adoptPersistedPair(persistedPair);
            return;
        }
        // Check if refresh has permanently failed - fail fast without network call
        if (refreshPermanentlyFailed) {
            console.log('[Trace] refreshOnce blocked - refresh permanently failed', {
                timestamp: new Date().toISOString()
            });
            throw new Error('Refresh token invalid. Please log in again.');
        }
        if (inflightRefresh) {
            // [AUTH-AUDIT] Log joining existing refresh
            console.log('[AUTH-AUDIT] AuthManager refreshOnce joining existing promise');
            return inflightRefresh;
        }
        // [AUTH-AUDIT] Log creating new refresh
        console.log('[AUTH-AUDIT] AuthManager refreshOnce creating new promise');
        inflightRefresh = (async () => {
            console.log('[Trace] refreshOnce start', {
                timestamp: new Date().toISOString()
            });
            const refreshToken = this.state.refreshToken;
            if (!refreshToken) {
                refreshPermanentlyFailed = true;
                throw new Error('No refresh token available');
            }
            const clearCountAtStart = this.clearCount;
            let clearedWhileInFlight = false;
            this.refreshingWith = refreshToken;
            this.pairClearedDuringRefresh = null;
            try {
                // Import AuthService dynamically to avoid circular dependencies
                const { AuthService } = await Promise.resolve().then(() => __importStar(require('./services')));
                const response = await AuthService.refreshToken(refreshToken);
                // Tokens were cleared (an explicit logout) while the request was in flight: do not store
                // the new pair.
                if (this.clearCount !== clearCountAtStart) {
                    clearedWhileInFlight = true;
                    throw Object.assign(new Error('Tokens were cleared during refresh.'), { status: 401 });
                }
                // Require both access and refresh tokens from backend (token rotation + blacklist)
                if (response.access && response.refresh) {
                    // Use new refresh token - never reuse old token (backend blacklists it)
                    this.setTokens(response.access, response.refresh);
                    // Reset failure flag on successful refresh
                    refreshPermanentlyFailed = false;
                    console.log('[Trace] refreshOnce success', {
                        timestamp: new Date().toISOString()
                    });
                }
                else if (response.access && !response.refresh) {
                    // Keep existing refresh token when backend returns access-only
                    this.setTokens(response.access, refreshToken);
                    refreshPermanentlyFailed = false;
                    console.warn('[AUTH-WARN] Refresh response missing refresh token; keeping existing refresh token.');
                }
                else {
                    // Missing access token
                    refreshPermanentlyFailed = true;
                    throw new Error('Invalid refresh response');
                }
            }
            catch (error) {
                // Another tab may have rotated the same token, possibly within the same second, while this
                // request was in flight. Use its pair instead of failing (callers would clear tokens), but
                // only if the client's own cleanup of a 401 on this refresh was the only clear: any other
                // clear was an explicit logout, after which nothing is restored.
                const ownCleanupClears = error?.status === 401 ? 1 : 0;
                if (!clearedWhileInFlight && this.clearCount - clearCountAtStart <= ownCleanupClears) {
                    const persistedPair = this.readNewerPersistedPair(refreshToken, true);
                    const rotatedPair = persistedPair ?? this.pairClearedDuringRefresh;
                    if (rotatedPair) {
                        if (!persistedPair) {
                            // The client's cleanup removed it; put it back for the other tabs.
                            try {
                                localStorage.setItem('access', rotatedPair.accessToken);
                                localStorage.setItem('refresh', rotatedPair.refreshToken);
                            }
                            catch (storageError) {
                                console.warn('Failed to restore tokens to localStorage:', storageError);
                            }
                        }
                        this.adoptPersistedPair(rotatedPair);
                        return;
                    }
                }
                // Mark refresh as permanently failed on any error
                refreshPermanentlyFailed = true;
                // Safely extract error message from unknown error type
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.log('[Trace] refreshOnce fail', {
                    error: errorMessage,
                    refreshPermanentlyFailed: true,
                    timestamp: new Date().toISOString()
                });
                throw error;
            }
            finally {
                this.refreshingWith = null;
                this.pairClearedDuringRefresh = null;
            }
        })().finally(() => {
            inflightRefresh = null;
        });
        return inflightRefresh;
    }
    // Check if refresh has permanently failed
    hasRefreshPermanentlyFailed() {
        return refreshPermanentlyFailed;
    }
}
// Export singleton instance
exports.authManager = new AuthManager();
